/**
 * Форма входа — общая для всех вариантов экрана входа.
 *
 * Основной способ — корпоративная учётная запись через Keycloak (кнопка
 * появляется, когда вход настроен на сервере). Вход по логину и паролю —
 * служебный, он скрыт под отдельной ссылкой.
 *
 * heading — заголовок над формой: строка или готовый элемент, если
 * варианту экрана нужен свой.
 */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { login, clearError } from '../../store/slices/authSlice';
import { api } from '../../api/client';
import styles from './LoginForm.module.css';

export default function LoginForm({ heading = 'Вход', className = '' }) {
  const dispatch = useDispatch();
  const { status, error } = useSelector((state) => state.auth);

  // null — ещё не известно: пока сервер не ответил, не показываем
  // ни кнопку, ни форму, чтобы экран не перестраивался на глазах.
  const [corporateAvailable, setCorporateAvailable] = useState(null);
  // Служебный вход по паролю скрыт по умолчанию: он предназначен для
  // одной учётной записи и не должен путать остальных сотрудников.
  const [serviceLoginOpen, setServiceLoginOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [corporateError, setCorporateError] = useState(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const submitting = status === 'submitting';

  // Доступность корпоративного входа сообщает сервер: показывать
  // кнопку, которая никуда не ведёт, хуже, чем не показывать вовсе.
  useEffect(() => {
    api.get('/api/health')
      .then((data) => setCorporateAvailable(Boolean(data.oidcEnabled)))
      .catch(() => setCorporateAvailable(false));
  }, []);

  async function startCorporateLogin() {
    setCorporateError(null);
    setRedirecting(true);
    try {
      const { authUrl } = await api.get('/api/auth/oidc/start');
      window.location.href = authUrl;
    } catch (err) {
      setRedirecting(false);
      setCorporateError(err.message);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!username || !password || submitting) return;
    dispatch(login({ username, password }));
  }

  function onField(setter) {
    return (e) => {
      setter(e.target.value);
      if (error) dispatch(clearError());
    };
  }

  const showPasswordForm = corporateAvailable === false || serviceLoginOpen;
  const shownError = corporateError || error;

  return (
    <div className={`${styles.form} ${className}`}>
      {typeof heading === 'string' ? <h2 className={styles.heading}>{heading}</h2> : heading}

      {corporateAvailable && (
        <>
          <p className={styles.note}>Войдите корпоративной учётной записью.</p>
          <button
            className={styles.primary}
            type="button"
            onClick={startCorporateLogin}
            disabled={redirecting}
          >
            {redirecting ? 'Открываю страницу входа…' : 'Войти по корпоративной учётной записи'}
          </button>
        </>
      )}

      {/* Ошибка показывается вне формы пароля: ошибка корпоративного
          входа приходит, когда эта форма скрыта.
          role="alert" — чтобы программы чтения с экрана сообщили
          об ошибке сразу, а не при следующем переходе по полям */}
      {shownError && <div className={styles.error} role="alert">{shownError}</div>}

      {corporateAvailable && (
        <button
          className={styles.serviceToggle}
          type="button"
          aria-expanded={serviceLoginOpen}
          aria-controls="service-login"
          onClick={() => setServiceLoginOpen((v) => !v)}
        >
          {serviceLoginOpen ? 'Скрыть служебный вход' : 'Служебный вход'}
        </button>
      )}

      {showPasswordForm && (
        <form
          id="service-login"
          className={corporateAvailable ? styles.service : styles.plain}
          onSubmit={handleSubmit}
        >
          {corporateAvailable && (
            <p className={styles.serviceNote}>
              Для администраторов сервиса, когда корпоративный вход недоступен.
            </p>
          )}

          <label className={styles.field}>
            <span className={styles.label}>Логин</span>
            <input
              className={styles.input}
              value={username}
              onChange={onField(setUsername)}
              autoComplete="username"
              autoFocus
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Пароль</span>
            <input
              className={styles.input}
              type="password"
              value={password}
              onChange={onField(setPassword)}
              autoComplete="current-password"
            />
          </label>

          <button
            className={corporateAvailable ? styles.secondary : styles.primary}
            type="submit"
            disabled={submitting}
          >
            {submitting ? 'Вхожу…' : 'Войти'}
          </button>
        </form>
      )}
    </div>
  );
}
